import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ContactForm } from "../types";
import EnhancedFormField from "./EnhancedFormField";
import EnhancedSubmitButton from "./EnhancedSubmitButton";
import {
  useAnimationConfig,
  useReducedMotion,
} from "../lib/animations/context";
import { trackFormSubmission } from "../lib/analytics";

interface EnhancedEnquiryFormProps {
  homeInterest?: string;
  onSubmit?: (data: ContactForm) => void;
  className?: string;
  showProgress?: boolean;
  variant?: "default" | "compact" | "detailed";
}

const EnhancedEnquiryForm: React.FC<EnhancedEnquiryFormProps> = ({
  homeInterest,
  onSubmit,
  className = "",
}) => {
  const [formData, setFormData] = useState<ContactForm>({
    name: "",
    email: "",
    phone: "",
    preferredContact: "email",
    message: "",
    consent: false,
    homeInterest: homeInterest || "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Partial<ContactForm>>({});
  const [touchedFields, setTouchedFields] = useState<Set<keyof ContactForm>>(
    new Set()
  );

  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  // Form validation functions
  const validateField = (
    field: keyof ContactForm,
    value: any
  ): string | null => {
    switch (field) {
      case "name":
        if (!value || value.trim().length < 2) {
          return "Name must be at least 2 characters long";
        }
        return null;

      case "email":
        if (!value || !value.trim()) {
          return "Email is required";
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          return "Please enter a valid email address";
        }
        return null;

      case "phone":
        if (!value || !value.trim()) {
          return "Phone number is required";
        }
        const phoneRegex = /^[\+]?[\d\s\-\(\)]{8,}$/;
        if (!phoneRegex.test(value.replace(/\s/g, ""))) {
          return "Please enter a valid phone number";
        }
        return null;

      case "message":
        if (!value || value.trim().length < 10) {
          return "Message must be at least 10 characters long";
        }
        return null;

      case "consent":
        if (!value) {
          return "You must agree to be contacted";
        }
        return null;

      default:
        return null;
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<ContactForm> = {};
    let isValid = true;

    (Object.keys(formData) as Array<keyof ContactForm>).forEach((field) => {
      if (field !== "homeInterest" && field !== "preferredContact") {
        const error = validateField(field, formData[field]);
        if (error) {
          newErrors[field] = error;
          isValid = false;
        }
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  // Calculate form progress
  const getFormProgress = () => {
    const requiredFields: Array<keyof ContactForm> = [
      "name",
      "email",
      "phone",
      "message",
      "consent",
    ];
    const completedFields = requiredFields.filter((field) => {
      const value = formData[field];
      return (
        value &&
        (typeof value === "boolean"
          ? value
          : value.toString().trim().length > 0)
      );
    });

    return {
      completed: completedFields.length,
      total: requiredFields.length,
      percentage: (completedFields.length / requiredFields.length) * 100,
    };
  };

  const progress = getFormProgress();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      // Mark all fields as touched to show errors
      setTouchedFields(
        new Set(Object.keys(formData) as Array<keyof ContactForm>)
      );
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const { growthtradiesApi } = await import("../services/api");

      // Build message with all details
      let message = formData.message;
      if (formData.homeInterest) {
        message = `Home of Interest: ${formData.homeInterest}\n\n${message}`;
      }
      if (formData.preferredContact) {
        message += `\n\nPreferred Contact Method: ${formData.preferredContact}`;
      }

      await growthtradiesApi.submitLead({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        job_description: message,
      });

      if (onSubmit) {
        onSubmit(formData);
      }

      trackFormSubmission({
        formName: "enhanced_enquiry",
        homeInterest: formData.homeInterest,
      });

      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitError("Failed to submit form. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    field: keyof ContactForm,
    value: string | boolean
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }

    // Clear submit error
    if (submitError) {
      setSubmitError(null);
    }
  };

  const handleFieldBlur = (field: keyof ContactForm) => {
    setTouchedFields((prev) => new Set([...prev, field]));
  };

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      preferredContact: "email",
      message: "",
      consent: false,
      homeInterest: homeInterest || "",
    });
    setErrors({});
    setTouchedFields(new Set());
    setIsSubmitted(false);
    setSubmitError(null);
  };

  // Animation variants
  const formVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: config.customTimings.normal,
        staggerChildren: 0.1,
      },
    },
  };

  const fieldVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: config.customTimings.fast },
    },
  };

  if (isSubmitted) {
    return (
      <motion.div
        className="bg-white rounded-2xl shadow-lg p-8"
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#1F2937]">Message Sent!</h3>
              <p className="text-sm text-[#334155]">We'll get back to you within 24 hours</p>
            </div>
          </div>
          <button
            onClick={resetForm}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <p className="text-[#334155] mb-6">
          Thank you for your interest! A member of our team will contact you shortly.
        </p>
        <button
          onClick={resetForm}
          className="w-full bg-[#FF5A1F] text-white py-3 px-6 rounded-lg font-semibold hover:bg-[#e4501c] transition-colors duration-200"
        >
          Send Another Message
        </button>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        className={`bg-white rounded-2xl shadow-lg p-8 ${className}`}
        variants={formVariants}
        initial="hidden"
        animate="visible"
      >
        <motion.h3
          className="text-2xl font-bold text-[#1F2937] mb-6"
          variants={fieldVariants}
        >
          Get In Touch
        </motion.h3>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Personal Information */}
          <motion.div
            variants={fieldVariants}
            className="grid grid-cols-1 md:grid-cols-2 gap-6"
          >
            <EnhancedFormField
              label="Full Name"
              value={formData.name}
              onChange={(value) => handleInputChange("name", value)}
              onBlur={() => handleFieldBlur("name")}
              error={touchedFields.has("name") ? errors.name : undefined}
              placeholder="Enter your full name"
              required
              validateOnType
              validator={(value) => validateField("name", value)}
            />

            <EnhancedFormField
              label="Email Address"
              type="email"
              value={formData.email}
              onChange={(value) => handleInputChange("email", value)}
              onBlur={() => handleFieldBlur("email")}
              error={touchedFields.has("email") ? errors.email : undefined}
              placeholder="Enter your email address"
              required
              validateOnType
              validator={(value) => validateField("email", value)}
            />
          </motion.div>

          {/* Contact Information */}
          <motion.div variants={fieldVariants}>
            <EnhancedFormField
              label="Phone Number"
              type="tel"
              value={formData.phone}
              onChange={(value) => handleInputChange("phone", value)}
              onBlur={() => handleFieldBlur("phone")}
              error={touchedFields.has("phone") ? errors.phone : undefined}
              placeholder="Enter your phone number"
              required
              validateOnType
              validator={(value) => validateField("phone", value)}
            />
          </motion.div>

          {/* Home Interest */}
          {homeInterest && (
            <motion.div variants={fieldVariants}>
              <EnhancedFormField
                label="Home of Interest"
                value={formData.homeInterest}
                onChange={(value) => handleInputChange("homeInterest", value)}
                disabled
                className="bg-gray-50"
              />
            </motion.div>
          )}

          {/* Message */}
          <motion.div variants={fieldVariants}>
            <EnhancedFormField
              label="Your Message"
              type="textarea"
              value={formData.message}
              onChange={(value) => handleInputChange("message", value)}
              onBlur={() => handleFieldBlur("message")}
              error={touchedFields.has("message") ? errors.message : undefined}
              placeholder="Tell us about your home building needs..."
              required
              rows={4}
              maxLength={1000}
              showCharacterCount
              validateOnType
              validator={(value) => validateField("message", value)}
            />
          </motion.div>

          {/* Consent */}
          <motion.div variants={fieldVariants}>
            <motion.label
              className={`flex items-start space-x-3 cursor-pointer p-3 rounded-lg border-2 transition-all duration-200 ${
                formData.consent
                  ? "border-[#FF5A1F]/30 bg-[#FF5A1F]/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              whileHover={!isReducedMotion ? { scale: 1.01 } : {}}
            >
              <input
                type="checkbox"
                checked={formData.consent}
                onChange={(e) => handleInputChange("consent", e.target.checked)}
                className="mt-1 text-[#FF5A1F] focus:ring-[#FF5A1F] focus:ring-2 rounded transition-all duration-200 w-4 h-4"
                key={`consent-${formData.consent}`}
              />
              <span className="text-sm text-[#334155] leading-relaxed">
                I agree to be contacted by Rolleston Homes regarding my enquiry.
                I understand that my information will be handled in accordance
                with the Privacy Policy.
              </span>
            </motion.label>
            {touchedFields.has("consent") && errors.consent && (
              <motion.p
                className="mt-2 text-sm text-red-600 flex items-center space-x-1"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <span>⚠️</span>
                <span>{errors.consent}</span>
              </motion.p>
            )}
          </motion.div>

          {/* Submit Error */}
          <AnimatePresence>
            {submitError && (
              <motion.div
                className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
              >
                {submitError}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Submit Button */}
          <motion.div variants={fieldVariants}>
            <EnhancedSubmitButton
              isLoading={isSubmitting}
              isError={!!submitError}
              disabled={progress.percentage < 100}
              fullWidth
              variant="primary"
              size="large"
              loadingText="Sending your enquiry..."
              errorText="Try Again"
            >
              Send Enquiry
            </EnhancedSubmitButton>
          </motion.div>
        </form>
      </motion.div>

    </>
  );
};

export default EnhancedEnquiryForm;
